import { WorkspaceRole } from './getWorkspaceMembers';
import { WorkspaceMemberRoleFloor } from './types';

/**
 * Privilege ranking for workspace roles, lowest to highest:
 *   Viewer < Editor < Admin < Owner
 *
 * NOTE: this is NOT the numeric value of the WorkspaceRole enum (those are
 * opaque bitmask values), so an explicit rank map is required.
 */
const ROLE_PRIVILEGE: Record<WorkspaceRole, number> = {
  [WorkspaceRole.Viewer]: 0,
  [WorkspaceRole.Editor]: 1,
  [WorkspaceRole.Admin]: 2,
  [WorkspaceRole.Owner]: 3,
};

const FLOOR_PRIVILEGE: Record<WorkspaceMemberRoleFloor, number> = {
  viewer: 0,
  editor: 1,
  admin: 2,
  owner: 3,
};

/**
 * Whether a member holding `role` satisfies a role-floor setting — i.e. their
 * role is at or above the minimum required. Mirrors the backend
 * `roleMeetsFloor`; used to gate UI such as the invite affordance against
 * `allow_member_invite`:
 *   - floor 'viewer' → everyone (Viewers, Editors, Admins, Owners)
 *   - floor 'editor' → Editors, Admins, Owners
 *   - floor 'admin'  → Admins, Owners
 *   - floor 'owner'  → Owner only
 */
export const roleMeetsFloor = (
  role: WorkspaceRole,
  floor: WorkspaceMemberRoleFloor
): boolean => ROLE_PRIVILEGE[role] >= FLOOR_PRIVILEGE[floor];

/**
 * Whether `role` is at or below `ceiling`'s privilege — i.e. a member can act
 * on `role` (e.g. select it in the invite dropdown). Both are member roles,
 * not floors.
 */
export const isRoleAtOrBelow = (
  role: WorkspaceRole,
  ceiling: WorkspaceRole
): boolean => ROLE_PRIVILEGE[role] <= ROLE_PRIVILEGE[ceiling];

/**
 * Whether the invite affordance (button / dialog) should be available to a
 * member. Owners and Admins always manage invites regardless of the floor;
 * everyone else needs to meet the `allow_member_invite` floor (to view & copy
 * existing links).
 *
 * The Owner/Admin bypass matches the backend `fetchWorkspaceCode`, which also
 * exempts them from the floor. (For the floors surfaced in the UI — viewer /
 * editor / admin — Owners/Admins satisfy the floor anyway, so the bypass only
 * matters for the reserved, UI-unreachable 'owner' floor.)
 */
export const canAccessInvites = (
  role: WorkspaceRole,
  floor: WorkspaceMemberRoleFloor
): boolean =>
  role === WorkspaceRole.Owner ||
  role === WorkspaceRole.Admin ||
  roleMeetsFloor(role, floor);
